import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProductStockDTO } from '../../../types/inventory/ProductStockTypes';

/**
 * Estado global para la gestión de stock de productos en inventario.
 */
interface InventoryStockState {
    list: ProductStockDTO[];    // Lista de stock paginado
    paginator: any;             // Datos de paginación
    isLoading: boolean;         // Indicador de carga de datos
    isEditModalOpen: boolean;   // Estado para abrir/cerrar modal de editar stock
    selectedStock: ProductStockDTO | null; // Producto seleccionado para editar
}

/**
 * Estado inicial para la gestión de stock.
 */
const initialState: InventoryStockState = {
    list: [],
    paginator: null,
    isLoading: false,
    isEditModalOpen: false,
    selectedStock: undefined
};

/**
 * Slice de Redux Toolkit para manejar el estado de stock de productos.
 */
const inventoryStockSlice = createSlice({
    name: 'inventoryStock',
    initialState,
    reducers: {
        /**
         * Establece la lista de stock y el paginador.
         */
        setStockList(state, action: PayloadAction<{ content: ProductStockDTO[]; paginator: any }>) {
            state.list = action.payload.content;
            state.paginator = action.payload.paginator;
        },

        /**
         * Limpia la lista de stock y resetea el paginador.
         */
        clearStockList(state) {
            state.list = [];
            state.paginator = null;
        },

        /**
         * Actualiza el indicador de carga (`isLoading`).
         */
        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },

        /**
         * Abre el modal de edición y establece el stock seleccionado.
         */
        openEditModal(state, action: PayloadAction<ProductStockDTO>) {
            state.selectedStock = action.payload;
            state.isEditModalOpen = true;
        },

        /**
         * Cierra el modal de edición y limpia el stock seleccionado.
         */
        closeEditModal(state) {
            state.selectedStock = null;
            state.isEditModalOpen = false;
        },

        /**
         * Actualiza los valores de stock mínimo y punto de reorden de un producto en la lista.
         */
        updateStockSettingsInList(state, action: PayloadAction<{ productId: number; branchId: number; minStock: number; reorderPoint: number }>) {
            const { productId, branchId, minStock, reorderPoint } = action.payload;

            const stockIndex = state.list.findIndex(
                (item) => item.productId === productId && item.branchId === branchId
            );

            if (stockIndex !== -1) {
                state.list[stockIndex].minStock = minStock;
                state.list[stockIndex].reorderPoint = reorderPoint;
            }
        },
    }
});

// Exportación de acciones
export const {
    setStockList,
    clearStockList,
    setIsLoading,
    openEditModal,
    closeEditModal,
    updateStockSettingsInList,
} = inventoryStockSlice.actions;

// Exportación del reducer
export default inventoryStockSlice.reducer;
